import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Chip
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import { toast } from 'react-toastify';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.identifier.trim()) {
      setError('Username or email is required');
      return;
    }
    if (!formData.password.trim()) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    const result = await login(formData.identifier, formData.password);

    if (result.success) {
      toast.success('Login successful!');
      navigate(from, { replace: true });
    } else {
      setError(result.error);
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 700,
          textAlign: 'center',
          mb: 1,
          color: 'text.primary'
        }}
      >
        Welcome Back
      </Typography>
      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          mb: 3,
          color: 'text.secondary',
          fontWeight: 500
        }}
      >
        Sign in to continue
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Username or Email"
        name="identifier"
        type="text"
        value={formData.identifier}
        onChange={handleChange}
        margin="normal"
        required
        autoComplete="username"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonOutlineIcon sx={{ color: '#6366f1' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: '#6366f1',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6366f1',
              borderWidth: '2px',
            },
          },
        }}
      />

      <TextField
        fullWidth
        label="Password"
        name="password"
        type="password"
        value={formData.password}
        onChange={handleChange}
        margin="normal"
        required
        autoComplete="current-password"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlinedIcon sx={{ color: '#ec4899' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: '#ec4899',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#ec4899',
              borderWidth: '2px',
            },
          },
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
        sx={{
          mt: 3,
          mb: 2,
          py: 1.5,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1rem',
          borderRadius: 3,
          boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.5)',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)',
            boxShadow: '0 15px 35px -5px rgba(99, 102, 241, 0.6)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        }}
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Link
          to="/auth/forgot-password"
          style={{
            color: '#6366f1',
            textDecoration: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.target.style.textDecoration = 'underline'; }}
          onMouseLeave={(e) => { e.target.style.textDecoration = 'none'; }}
        >
          Forgot Password?
        </Link>
      </Box>

      <Divider sx={{ my: 2 }}>
        <Chip label="or" size="small" sx={{ color: 'text.secondary', fontWeight: 500 }} />
      </Divider>

      <Typography variant="body2" align="center" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        Don't have an account?{' '}
        <Link
          to="/auth/register"
          style={{
            color: '#6366f1',
            textDecoration: 'none',
            fontWeight: 700,
            borderBottom: '2px solid transparent',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => { e.target.style.borderBottom = '2px solid #6366f1'; }}
          onMouseLeave={(e) => { e.target.style.borderBottom = '2px solid transparent'; }}
        >
          Create Account
        </Link>
      </Typography>
    </Box>
  );
};

export default Login;
